<template>
  <div>
    <navbar :links="links" />
    <!-- Link to current plan bookings relevant for the User -->
    <v-slide-y-reverse-transition>
      <p
        v-if="bookings.length > 0"
        class="mt-4 grey--text text--darken-3"
        style="font-size: larger"
      >
        <v-layout align-center>
          <router-link
            :to="{name: 'contentcreatorbilling'}"
            data-cy="contentcreator-plans-billing-link"
          >
            {{ $t('contentCreatorBillingLink') }}
          </router-link>
        </v-layout>
      </p>
    </v-slide-y-reverse-transition>
    <!-- Active Booking Request? -->
    <v-slide-y-reverse-transition>
      <content-creator-active-booking-request-hint
        v-if="activeBookingRequest"
        :booking-request="activeBookingRequest"
        :class="{clickable: isActiveBookingRequestClickable}"
        @click.native="onActiveBookingRequestClicked"
      />
    </v-slide-y-reverse-transition>
    <!-- Header -->
    <v-img
      src="/static/contentcreatorlanding_header.png"
      contain
      width="90%"
      max-height="70%"
      style="pointer-events: none; position: absolute; left: 5%; opacity: 0.065"
      :style="{
        top: showPlanCards ? null : '20%',
        bottom: showPlanCards ? '2.5%' : null,
      }"
    />
    <p class="mt-4 subheading">
      {{ $t('contentCreatorPageHeader') }}
    </p>
    <!-- Loading indicator -->
    <v-layout
      v-if="loading"
      justify-center
    >
      <v-progress-circular
        indeterminate
        style="position: fixed; z-index: 5"
        class="mt-5 msr--text"
        :size="50"
      />
    </v-layout>
    <!-- Monthly / Yearly switch -->
    <v-scale-transition>
      <v-layout
        v-if="
          !loading &&
            (availablePlans
              ? !shouldHideActionsForPlan(availablePlans[0])
              : false) &&
            showPlanCards
        "
        justify-center
        align-center
        class="mt-3"
      >
        <v-btn
          class="ma-0"
          :class="{
            'ml-3': $vuetify.breakpoint.mdAndUp,
            'elevation-0 grey--text': !monthlyBillingChosen,
            'elevation-5 white': monthlyBillingChosen,
          }"
          style="z-index: 1; position: relative; bottom: -4px"
          :flat="!monthlyBillingChosen"
          :large="$vuetify.breakpoint.mdAndUp"
          @click="billingPeriod = 'MONTHLY'"
        >
          {{ $t('contentCreatorPlanBillingMonthly') }}
        </v-btn>
        <v-btn
          class="ma-0"
          :class="{
            'elevation-0 grey--text': !yearlyBillingChosen,
            'elevation-5 white': yearlyBillingChosen,
          }"
          style="z-index: 1; position: relative; bottom: -4px"
          :flat="!yearlyBillingChosen"
          :large="$vuetify.breakpoint.mdAndUp"
          @click="billingPeriod = 'YEARLY'"
        >
          {{ $t('contentCreatorPlanBillingYearly') }}
        </v-btn>
      </v-layout>
    </v-scale-transition>
    <!-- Plans list -->
    <v-scale-transition>
      <v-layout
        v-if="availablePlans.length > 0 && showPlanCards"
        wrap
        justify-center
      >
        <content-creator-plan-card
          v-for="(plan, planIndex) in availablePlans"
          :key="plan.id"
          :plan="plan"
          :show-test-period="canDoTestBooking"
          :show-decline-another-test-button="
            canDoTestBooking && hadPriorTestRun
          "
          :billing-period="billingPeriod"
          :hide-actions="shouldHideActionsForPlan(plan)"
          :show-name="availablePlans.length > 1"
          :no-left-margin="planIndex === 0"
          :no-right-margin="planIndex === plans.length - 1"
          :max-width="planCardMaxWidth"
          class="elevation-5"
          style="z-index: 2"
          @selected="onPlanSelectedForBooking(plan)"
          @selected-decline-another-test="onPlanSelectedForBooking(plan, true)"
        />
      </v-layout>
    </v-scale-transition>
    <!-- Scroll space -->
    <div style="min-height: 60px" />
    <!-- Dialog: Do a booking -->
    <content-creator-plan-booking-dialog
      :show.sync="showBookingDialog"
      :plan="selectedPlanToBook"
      :is-test-booking="canDoTestBooking && !declineAnotherTest"
      :show-name="availablePlans.length > 1"
      :billing-period.sync="billingPeriod"
      :workspace-filter="filterBookableWorkspacesForSelectedPlan"
      :busy="busyBooking"
      @aborted="onBookingAborted"
      @confirmed="bookOrRequestBookPlan"
    />
    <!-- Dialog: Ask User to create a Branding -->
    <create-or-skip-branding-dialog
      :show.sync="showCreateOrSkipBrandingDialog"
      @skip="$router.push({name: 'tvtemplates'})"
      @create="$router.push({name: 'branding', query: {create: true}})"
    />
    <!-- Dialog: Feedback after successfully doing a paid booking -->
    <v-dialog
      v-model="showSuccessfullyDidPaidBookingDialog"
      :width="480"
      lazy
    >
      <v-card>
        <v-card-title class="pb-0">
          <v-layout align-center>
            <span class="title">{{
              $t('contentCreatorBookingSuccessDialogTitle')
            }}</span>
            <v-btn
              class="ml-auto mr-0 my-0"
              flat
              icon
              :title="$t('close')"
              @click="onSuccessfullyBookedDialogClosed()"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-layout>
          <div class="subheading">
            {{ $t('contentCreatorBookingSuccessDialogSubtitle') }}.
          </div>
        </v-card-title>
        <v-card-text class="py-0">
          <div
            class="clickable"
            :title="$t('goToTVTemplates')"
            @click="onSuccessfullyBookedDialogConfirmed()"
          >
            <!-- https://lottiefiles.com/64787-success -->
            <lottie-player
              :key="showSuccessfullyDidPaidBookingDialog"
              src="https://assets2.lottiefiles.com/packages/lf20_y2hxPc.json"
              background="transparent"
              speed="0.66"
              :style="{
                'min-width': $vuetify.breakpoint.mdAndUp ? '448px' : 'initial',
                'min-height': $vuetify.breakpoint.mdAndUp ? '460px' : 'initial',
              }"
              style="width: 100%; height: auto"
              autoplay
            />
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-layout justify-center>
            <v-btn
              round
              dark
              color="msr"
              data-cy="first-branding-created-dialog-to-tvtemplates-btn"
              @click="onSuccessfullyBookedDialogConfirmed()"
            >
              {{ $t('goToTVTemplates') }}
            </v-btn>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {importLottiePlayer} from '@/deferredImports';

import {makeAdminNamesString} from '@/mixins/commonUsermanagement.js';

import ContentCreatorActiveBookingRequestHint from '@/components/ContentCreatorActiveBookingRequestHint';
import ContentCreatorPlanBookingDialog from '@/components/ContentCreatorPlanBookingDialog';
import ContentCreatorPlanCard from '@/components/ContentCreatorPlanCard';
import CreateOrSkipBrandingDialog from '@/components/CreateOrSkipBrandingDialog';
import Navbar from '@/components/Navbar';

export default {
  components: {
    ContentCreatorActiveBookingRequestHint,
    ContentCreatorPlanBookingDialog,
    ContentCreatorPlanCard,
    CreateOrSkipBrandingDialog,
    Navbar,
  },
  data() {
    return {
      lottiePlayer: null,

      links: ['dashboard', 'contentcreatorplans'],
      loading: false,

      plans: [],
      billingPeriod: 'YEARLY',

      busyBooking: false,
      declineAnotherTest: false,
      selectedPlanToBook: null,
      showBookingDialog: false,
      showCreateOrSkipBrandingDialog: false,
      showSuccessfullyDidPaidBookingDialog: false,
    };
  },
  computed: {
    /**
     * Hide them while the dialog is opened when triggered via URL query.
     *
     * We don't want to show confusing price info e.g. when going
     * straight to the start-testrun dialog.
     */
    showPlanCards() {
      return !this.showBookingDialog;
    },
    canDoTestBooking() {
      return this.$store.state.common.contentcreatorCanDoTestBooking;
    },
    hadPriorTestRun() {
      return (
        this.$userprofile &&
        this.$userprofile.content_creator_plan_booking &&
        this.$userprofile.content_creator_plan_booking.test_has_expired
      );
    },
    monthlyBillingChosen() {
      return this.billingPeriod === 'MONTHLY';
    },
    yearlyBillingChosen() {
      return this.billingPeriod === 'YEARLY';
    },
    bookingRequests() {
      return this.$store.state.common.contentcreatorBookingRequests;
    },
    activeBookingRequest() {
      if (!this.bookingRequests.length) {
        return null;
      }
      return this.bookingRequests[0];
    },
    isActiveBookingRequestClickable() {
      return (
        !this.$store.getters.userIsBeingManaged ||
        this.$store.getters.isManagedCoadmin
      );
    },
    bookings() {
      return this.$store.state.common.contentcreatorBookings;
    },
    availablePlans() {
      return this.plans.filter((plan) => plan.available_to_user);
    },
    planCardMaxWidth() {
      if (this.availablePlans.length === 1) {
        return '440px';
      }
      return 100.0 / this.availablePlans.length - 2 + '%';
    },
    planViaQueryParam() {
      let planIdOrName = this.$route.query.plan;
      if (!planIdOrName) {
        return null;
      }
      planIdOrName = String(planIdOrName).toLowerCase();

      const candidates = this.availablePlans.filter((plan) => {
        return (
          plan.id == planIdOrName || plan.name.toLowerCase() == planIdOrName
        );
      });
      if (!candidates.length) {
        return null;
      }
      return candidates[0] || null;
    },
  },
  watch: {
    showBookingDialog: function (showBookingDialog) {
      if (!showBookingDialog) {
        // Reset temporary state modified by button on Plan card.
        this.declineAnotherTest = false;
      }
    },
  },
  created() {
    this.$store.dispatch('MAYBE_RETRIEVE_CURRENT_USER').then(() => {
      this.fetchCanDoTestBooking();
      this.fetchPlanData().then(() => {
        this.maybeOpenDialogForPlanFromQuery();
      });
    });

    importLottiePlayer().then((lib) => (this.lottiePlayer = lib));
  },
  methods: {
    onSuccessfullyBookedDialogClosed() {
      this.showSuccessfullyDidPaidBookingDialog = false;
    },
    onSuccessfullyBookedDialogConfirmed() {
      this.onSuccessfullyBookedDialogClosed();

      // Redirect user to tvtemplates page.
      this.$router.push({name: 'tvtemplates'});
    },
    onActiveBookingRequestClicked() {
      if (
        !this.isActiveBookingRequestClickable ||
        this.availablePlans.length == 0
      ) {
        return;
      }
      const firstPlan = this.availablePlans[0];
      this.onPlanSelectedForBooking(firstPlan);
    },
    shouldHideActionsForPlan(plan) {
      if (
        this.$store.getters.userIsBeingManaged &&
        !this.$store.getters.isManagedCoadmin &&
        !!this.activeBookingRequest
      ) {
        return true;
      }

      const hasBooking =
        this.$userprofile && !!this.$userprofile.content_creator_plan_booking;
      if (!hasBooking) {
        return false;
      }

      const hasTestBooking =
        hasBooking && this.$userprofile.content_creator_plan_booking.is_test;
      const hasBilledBooking =
        hasBooking &&
        this.$userprofile.content_creator_plan_booking.is_currently_billed;
      const hasCancelledBooking =
        hasBooking &&
        !!this.$userprofile.content_creator_plan_booking.cancelled_at;
      const hasSameOrHigherPlanBooking =
        hasBooking &&
        !!plan &&
        this.$userprofile.content_creator_plan_booking.plan.level >= plan.level;

      // When a paid booking has been cancelled, show the button.
      if (hasCancelledBooking) {
        return false;
      }

      if (hasTestBooking) {
        return false;
      }

      return hasBilledBooking || hasSameOrHigherPlanBooking;
    },
    /**
     * Display a booking dialog for e.g. '?plan=123' if specified in the URL.
     *
     * The 'plan' is matched as either id or (case insensitive) name, so
     * '?plan=pro' also works.
     */
    maybeOpenDialogForPlanFromQuery() {
      this.onPlanSelectedForBooking(this.planViaQueryParam);
    },
    fetchCanDoTestBooking() {
      this.$store.dispatch('RETRIEVE_CONTENTCREATOR_CAN_DO_TEST_BOOKING');
    },
    fetchContentCreatorPlans() {
      this.$api
        .listContentCreatorPlans()
        .then((response) => (this.plans = response.data));
    },
    fetchContentCreatorBookingRequests() {
      return this.$store.dispatch('LIST_CONTENTCREATOR_BOOKING_REQUESTS');
    },
    fetchContentCreatorPlanBookings() {
      return this.$store.dispatch('LIST_CONTENTCREATOR_BOOKINGS');
    },
    fetchPlanData() {
      this.loading = true;
      const plansPromise = this.fetchContentCreatorPlans();
      const bookingsPromise = this.fetchContentCreatorPlanBookings();
      const requestsPromise = this.fetchContentCreatorBookingRequests();
      return Promise.all([
        plansPromise,
        bookingsPromise,
        requestsPromise,
      ]).finally(() => (this.loading = false));
    },
    /**
     * Limit which Groups are offered by the select
     * based on existing bookings and business logic.
     */
    filterBookableWorkspacesForSelectedPlan(bookableWorkspaces) {
      const noPlanSelected = this.selectedPlanToBook === null;
      if (noPlanSelected) {
        return [];
      }

      return bookableWorkspaces.filter((workspace) => {
        const existingActualBookings = this.bookings.filter((booking) => {
          return (
            booking.workspace &&
            booking.workspace.id == workspace.id &&
            !booking.cancelled_at &&
            !booking.is_test
          );
        });

        // Allow only association Groups/Workspaces if Plan is marked as such.
        const isAssociationPlan =
          this.selectedPlanToBook.can_target_association_groups;
        const isAssociationWorkspace = workspace.contains_association_groups;
        const mustCheckForAssociation =
          isAssociationPlan || isAssociationWorkspace;
        if (mustCheckForAssociation) {
          if (isAssociationPlan && !isAssociationWorkspace) {
            // The Plan is only allowed to book for association Groups/Workspaces.
            return false;
          }
          if (isAssociationWorkspace && !isAssociationPlan) {
            // The association Group/Workspace is not viable for this Plan.
            return false;
          }
        }

        return (
          existingActualBookings.length === 0 ||
          existingActualBookings[0].plan.level < this.selectedPlanToBook.level
        );
      });
    },
    onPlanSelectedForBooking(plan, declineAnotherTest = false) {
      if (!plan) {
        return;
      }
      if (declineAnotherTest) {
        this.declineAnotherTest = true;
      }
      this.selectedPlanToBook = plan;
      this.showBookingDialog = true;
    },
    onBookingAborted() {
      // Let animation of closing dialog finish, then reset selected plan.
      setTimeout(() => (this.selectedPlanToBook = null), 350);
    },
    /**
     * Decide whether to ask for booking or book directly.
     *
     * Args:
     *
     *   payload (Object):
     *     - planId: Number
     *     - workspaceId: Number
     *     - isTest: Boolean
     *     - billingPeriod: String ('YEARLY' or 'MONTHLY')
     *     - mustRequestBookingFromAdmin: Boolean
     */
    bookOrRequestBookPlan(payload) {
      if (!payload.isTest && payload.mustRequestBookingFromAdmin) {
        this.requestBookPlan(payload);
      } else {
        this.bookPlan(payload);
      }
    },
    /**
     * Send email to admins to ask to book the selected Plan.
     */
    requestBookPlan(payload) {
      this.busyBooking = true;
      this.$api
        .requestBookContentCreatorPlan(
          payload.planId,
          payload.workspaceId,
          payload.billingPeriod
        )
        .then(() => {
          // Fetch latest state so things like the produce-template button reflect it.
          this.$store.dispatch('RETRIEVE_CURRENT_USER');
          this.$store.dispatch('LIST_CONTENTCREATOR_BOOKING_REQUESTS');
          this.$store.dispatch('LIST_CONTENTCREATOR_BOOKINGS');

          const adminsString = makeAdminNamesString(
            payload.manager,
            payload.coadmins
          );

          this.$bus.$emit('show-dynamic-dialog', {
            title: this.$t('contentCreatorBookingDialogRequestSubmittedTitle'),
            titleIcon: 'email',
            titleIconClass: 'msr--text',
            content: `
              <div class="subheading">
                <p>${this.$t(
                  'contentCreatorBookingDialogRequestSubmittedBody'
                )}:</p>
                <p class="msr--text">
                  ${adminsString}
                </p>
              </div>
            `,
            persistent: true,
            showAbort: false,
            confirmIcon: 'check',
            onConfirm: function () {
              this.hideDialog_();
            },
          });
          this.$bus.$emit('alert-success', {
            message: 'contentCreatorBookingDialogRequestSubmittedTitle',
            translate: true,
            icon: 'email',
            timeout: 10000,
          });
          this.$router.push({name: 'tvtemplates'});
        })
        .finally(() => {
          this.busyBooking = false;
          this.showBookingDialog = false;
        });
    },
    /**
     * Book the selected Plan.
     */
    bookPlan(payload) {
      this.busyBooking = true;
      this.$api
        .bookContentCreatorPlan(
          payload.planId,
          payload.workspaceId,
          payload.isTest,
          payload.billingPeriod
        )
        .then(() => {
          this.$bus.$emit('alert-success', {
            message: 'contentCreatorBookingDialogConfirmed',
            translate: true,
            timeout: 10000,
          });

          this.fetchContentCreatorPlanBookings();

          // Fetch latest user.profile.content_creator_plan. We need it
          // on the next page to determine another potential redirect.
          this.$store.dispatch('RETRIEVE_CURRENT_USER').then(() => {
            this.$store.commit(
              'SET_CONTENTCREATOR_DRAWER_ITEMS_EXPANDED',
              true
            );

            // Cleanup
            this.busyBooking = false;
            this.showBookingDialog = false;

            // When a testrun, ask the User to create a Branding.
            if (payload.isTest) {
              this.showCreateOrSkipBrandingDialog = true;
            }
            // Otherwise (aka after a paid booking), show a feedback dialog.
            else {
              this.showSuccessfullyDidPaidBookingDialog = true;
            }
          });
        });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/ContentCreatorPlans.vue